// src/utils/api.js - API client
import axios from 'axios';
import i18n from './i18n';

// API URL resolution:
// - If REACT_APP_API_URL is set, always use it.
// - In local development (CRA dev server on :3000), default to Node backend on :4000.
// - In production behind nginx, use same-origin proxy (empty baseURL).
const API_URL =
  process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'development' ? 'http://localhost:4000' : '');

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token and language to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Send current language from localStorage or i18n (represents current session language)
  // This takes priority over saved preferences since it reflects the user's current UI language
  const currentLanguage = localStorage.getItem('language') || i18n.language || 'en';
  config.headers['X-Language'] = currentLanguage;

  // When sending FormData, do not set Content-Type so the browser sets
  // multipart/form-data with the correct boundary (required for multer/backend parsing)
  if (config.data && typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect if we're not already on login/register pages
      // This prevents page refresh when showing verification errors
      const currentPath = window.location.pathname;
      const isAuthPage = currentPath === '/login' || currentPath === '/register' || currentPath.startsWith('/verify-email');
      
      if (!isAuthPage) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

